// Libraries
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { ClearIcon } from "@mui/x-date-pickers";
import { AutocompleteRenderGetTagProps, Chip, Tooltip } from "@mui/material";

// Layouts
import {
	OFFSET_DISTANCE,
	OFFSET_SKIDDING,
	TooltipTitleWrapper,
} from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { CustomTooltipProps } from "@/Layouts/components/Tables/DataSmartTable/types";
import { Property } from "csstype";

interface ChipWithTooltipProps {
	option: { label: string; value: string };
	index: number;
	value: { label: string; value: string }[];
	getTagProps: AutocompleteRenderGetTagProps;
	disabled?: boolean;
	width: {
		default?: Property.Width<(string & {}) | number | 0> | undefined;
		max?: Property.Width<(string & {}) | number | 0> | undefined;
	};
}

function ChipWithTooltip(props: Readonly<ChipWithTooltipProps>) {
	const { option, index, value, width, getTagProps, disabled = false } = props;
	const { default: defaultWith = "90px", max: maxWidth = "90px" } = width ?? {};

	const chipRef = useRef<HTMLDivElement>(null);
	const label = option.label?.toUpperCase();
	const [isOverflown, setIsOverflown] = useState(false);

	const { key, ...rest } = getTagProps({ index });

	const deleteIcon = useMemo(() => {
		return index !== value.length - 1 || disabled ? <></> : <ClearIcon />;
	}, [value, index, disabled]);

	useLayoutEffect(() => {
		if (chipRef?.current?.firstChild) {
			const span = chipRef.current.firstChild as HTMLSpanElement;
			setIsOverflown(span.scrollWidth > span.clientWidth);
		}
	}, [chipRef, chipRef?.current?.firstChild]);

	const chip = useMemo(
		() => (
			<Chip
				key={`chip-${index ?? key}`}
				ref={chipRef}
				{...rest}
				deleteIcon={deleteIcon}
				label={label}
				size="small"
				sx={{
					width: defaultWith,
					maxWidth: `${maxWidth} !important`,
					borderRadius: "4px",
					justifyContent: "space-between",
					fontSize: "12px",
					fontFamily: "var(--font-family-v2)",
					color: "var(--text-color)",
				}}
			/>
		),
		[isOverflown, index, key, label, rest, deleteIcon]
	);

	if (!isOverflown) {
		return chip;
	}

	const wrappedTitle = <TooltipTitleWrapper text={label} />;
	const customTooltipProps: CustomTooltipProps = { arrow: true, placement: "top" };
	const offset = [OFFSET_SKIDDING, OFFSET_DISTANCE];
	customTooltipProps.slotProps = { popper: { modifiers: [{ name: "offset", options: { offset } }] } };

	return (
		<Tooltip title={wrappedTitle} {...customTooltipProps}>
			{chip}
		</Tooltip>
	);
}

export { ChipWithTooltip };
export default ChipWithTooltip;
